import { useState, useEffect, useRef } from "react";
import { addToWatchlist } from "../../services/watchlist.service";
import { useAuthFetch } from "../../services/clerk.service";

const fmt = {
  usd: (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`),
  pct: (v) =>
    v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`,
};

const WatchlistAdd = ({ onAdded }) => {
  const [query, setQuery] = useState("");
  const [ticker, setTicker] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [alertType, setAlertType] = useState("CONVICTION");
  const [threshold, setThreshold] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const authFetch = useAuthFetch();

  // Autocomplete
  useEffect(() => {
    const q = query.trim().toUpperCase();
    if (!q || ticker) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const timeout = setTimeout(async () => {
      try {
        const res = await authFetch(`/tickers?q=${encodeURIComponent(q)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data);
          setShowDropdown(data.length > 0);
        }
      } catch {}
    }, 200);

    return () => clearTimeout(timeout);
  }, [query, ticker, authFetch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectTicker = async (t) => {
    setTicker(t);
    setQuery(t);
    setShowDropdown(false);
    setSuggestions([]);
    setPreview(null);
    setPreviewLoading(true);
    setError("");

    try {
      const res = await authFetch(
        `/stock/preview?ticker=${encodeURIComponent(t)}`,
      );
      if (res.ok) setPreview(await res.json());
    } catch {
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setQuery(val);
    if (ticker && val !== ticker) {
      setTicker("");
      setPreview(null);
    }
  };

  const handleSubmit = async () => {
    const t = ticker || query.trim().toUpperCase();
    if (!t) return;
    if (alertType === "UPSIDE" && !threshold) {
      setError("Enter a threshold percentage");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const item = await addToWatchlist(authFetch, {
        ticker: t,
        alert_type: alertType,
        threshold_pct:
          alertType === "UPSIDE" ? parseFloat(threshold) : undefined,
      });
      setQuery("");
      setTicker("");
      setThreshold("");
      setAlertType("CONVICTION");
      setPreview(null);
      onAdded(item);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const upsideColor = preview
    ? preview.upside_pct >= 0
      ? "#34d399"
      : "#f87171"
    : "#9ca3af";

  return (
    <div className="wa-wrap">
      <p className="wa-label">Add to watchlist</p>

      {/* ── Input row ── */}
      <div className="wa-row">
        {/* Autocomplete input */}
        <div className="wa-autocomplete-wrap">
          <input
            ref={inputRef}
            className="wa-input"
            type="text"
            placeholder="Search ticker — e.g. AAPL"
            value={query}
            onChange={handleInputChange}
            onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (suggestions.length > 0) selectTicker(suggestions[0]);
                else handleSubmit();
              }
              if (e.key === "Escape") setShowDropdown(false);
            }}
            maxLength={10}
            autoComplete="off"
            spellCheck="false"
          />
          {showDropdown && (
            <div className="wa-dropdown" ref={dropdownRef}>
              {suggestions.map((s) => (
                <button
                  key={s}
                  className="wa-suggestion"
                  onMouseDown={() => selectTicker(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Alert type toggle */}
        <div className="wa-type-toggle">
          <button
            className={`wa-type-btn${alertType === "CONVICTION" ? " active" : ""}`}
            onClick={() => setAlertType("CONVICTION")}
          >
            Conviction
          </button>
          <button
            className={`wa-type-btn${alertType === "UPSIDE" ? " active" : ""}`}
            onClick={() => setAlertType("UPSIDE")}
          >
            Upside %
          </button>
        </div>

        <button
          className="wa-add-btn"
          onClick={handleSubmit}
          disabled={loading || !query.trim()}
        >
          {loading ? "Adding…" : "+ Add"}
        </button>
      </div>

      {/* ── Upside threshold ── */}
      {alertType === "UPSIDE" && (
        <div className="wa-threshold-row">
          <span className="wa-threshold-label">
            Notify me when upside crosses
          </span>
          <div className="wa-pct-wrap">
            <input
              className="wa-pct-input"
              type="number"
              placeholder="20"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              min={-100}
              max={200}
            />
            <span className="wa-pct-suffix">%</span>
          </div>
          <span className="wa-threshold-hint">
            {preview ? `Current upside: ` : ""}
            {preview && (
              <span
                style={{
                  color: upsideColor,
                  fontFamily: "'DM Mono', monospace",
                }}
              >
                {fmt.pct(preview.upside_pct)}
              </span>
            )}
          </span>
        </div>
      )}

      {/* ── Stock preview card ── */}
      {(previewLoading || preview) && (
        <div className="wa-preview">
          {previewLoading && (
            <div className="wa-preview-loading">
              <div className="wa-preview-spinner" />
              <span>Fetching stock data…</span>
            </div>
          )}
          {!previewLoading && preview && (
            <>
              <div className="wa-preview-header">
                <span className="wa-preview-ticker">{preview.ticker}</span>
                <span className="wa-preview-meta">
                  {preview.sector}
                  {preview.industry ? ` · ${preview.industry}` : ""}
                </span>
              </div>
              <div className="wa-preview-stats">
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">Price</span>
                  <span className="wa-preview-stat-val">
                    {fmt.usd(preview.current_price)}
                  </span>
                </div>
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">Intrinsic Value</span>
                  <span
                    className="wa-preview-stat-val"
                    style={{ color: "#34d399" }}
                  >
                    {fmt.usd(preview.average_intrinsic_value)}
                  </span>
                </div>
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">Upside</span>
                  <span
                    className="wa-preview-stat-val"
                    style={{ color: upsideColor }}
                  >
                    {fmt.pct(preview.upside_pct)}
                  </span>
                </div>
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">DCF</span>
                  <span className="wa-preview-stat-val">
                    {fmt.usd(preview.dcf_intrinsic_value)}
                  </span>
                </div>
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">EPV</span>
                  <span className="wa-preview-stat-val">
                    {fmt.usd(preview.epv_intrinsic_value)}
                  </span>
                </div>
                <div className="wa-preview-stat">
                  <span className="wa-preview-stat-label">Net Cash</span>
                  <span
                    className="wa-preview-stat-val"
                    style={{
                      color: preview.net_cash >= 0 ? "#34d399" : "#f87171",
                    }}
                  >
                    {preview.net_cash >= 0
                      ? `$${(preview.net_cash / 1e9).toFixed(1)}B`
                      : `-$${(Math.abs(preview.net_cash) / 1e9).toFixed(1)}B`}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {error && <p className="wa-error">{error}</p>}
    </div>
  );
};

export default WatchlistAdd;
